import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Download } from "lucide-react";
import { useHabitContext } from "../../context/HabitContext";
import { DayEntry } from "../../types";
import {
  getDaysInMonth,
  getDayOfWeek,
  getMonthName,
  getMonthShort,
  isToday,
  isWeekend,
} from "../../utils/dateUtils";
import { downloadCsv } from "../../utils/csv";
import {
  calculateCheckboxCompletion,
  calculateRatingAverage,
} from "../../utils/percentageUtils";
import HabitTable, { HabitTableRow } from "../common/HabitTable";
import SectionHeader from "../common/SectionHeader";
import Spinner from "../common/Spinner";
import "./MonthlyReadOnlyView.scss";

interface MonthlyReadOnlyViewProps {
  headerActions?: React.ReactNode;
}

const MonthlyReadOnlyView: React.FC<MonthlyReadOnlyViewProps> = ({ headerActions }) => {
  const { formatMessage: t, locale } = useIntl();
  const {
    categories,
    currentMonth,
    currentYear,
    getHabitEntriesFor,
    toggleHabitOn,
    setRatingOn,
    isLoading,
  } = useHabitContext();

  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [viewYear, setViewYear] = useState(currentYear);

  const navigate = (delta: number) => {
    setViewMonth(prev => {
      const next = prev + delta;
      if (next < 0) {
        setViewYear(y => y - 1);
        return 11;
      }
      if (next > 11) {
        setViewYear(y => y + 1);
        return 0;
      }
      return next;
    });
  };

  const typeOrder: Record<string, number> = {
    checkbox: 0,
    rating: 1,
  };

  const allHabits = categories
    .flatMap((c) => c.habits)
    .sort((a, b) => (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99));

  const hasHabits = allHabits.length > 0;
  const daysInMonth = getDaysInMonth(viewMonth, viewYear);

  const allEntries = Object.fromEntries(
    allHabits.map((h) => [h.id, getHabitEntriesFor(h.id, viewMonth, viewYear)]),
  );

  // Returns true when a day is before the habit's creation date
  const isDisabledCell = (habitId: string, day: number): boolean => {
    const habit = allHabits.find(h => h.id === habitId);
    if (!habit?.createdAt) return false;
    const created = new Date(habit.createdAt);
    const cy = created.getFullYear();
    const cm = created.getMonth();
    const cd = created.getDate();
    if (viewYear > cy) return false;
    if (viewYear === cy && viewMonth > cm) return false;
    if (viewYear === cy && viewMonth === cm && day >= cd) return false;
    return true;
  };

  // Effective day count for a habit (from its createdAt in this month)
  const effectiveDays = (habit: any): number => {
    if (!habit.createdAt) return daysInMonth;
    const created = new Date(habit.createdAt);
    const cy = created.getFullYear();
    const cm = created.getMonth();
    const cd = created.getDate();
    if (viewYear > cy || (viewYear === cy && viewMonth > cm)) return daysInMonth;
    if (viewYear === cy && viewMonth === cm) return Math.max(0, daysInMonth - cd + 1);
    return 0; // entire month is before creation
  };

  const rows: HabitTableRow[] = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return {
      key: String(day),
      day,
      dayNum: day,
      dow: getDayOfWeek(day, viewMonth, viewYear),
      isToday: isToday(day, viewMonth, viewYear),
      isWeekend: isWeekend(day, viewMonth, viewYear),
    };
  });

  const renderSummary = (habit: any) => {
    const entries = allEntries[habit.id];
    if (habit.type === "checkbox")
      return `${calculateCheckboxCompletion(entries, effectiveDays(habit))}`;
    if (habit.type === "rating") {
      const avg = calculateRatingAverage(entries);
      return avg > 0 ? avg.toFixed(1) : "—";
    }
    return "—";
  };

  const exportMonthCsv = () => {
    const headers = ['date', 'habit', 'type', 'value'];
    const csvRows: (string | number)[][] = [];
    const month = String(viewMonth + 1).padStart(2, '0');
    allHabits.forEach((h) => {
      const entries = allEntries[h.id] ?? [];
      for (let d = 1; d <= daysInMonth; d += 1) {
        const entry = entries.find((e: DayEntry) => e.day === d);
        const value = h.type === 'checkbox' ? (entry?.completed ? 1 : 0) : (entry?.rating ?? '');
        csvRows.push([
          `${viewYear}-${month}-${String(d).padStart(2, '0')}`,
          h.name,
          h.type,
          value,
        ]);
      }
    });
    downloadCsv(`habits-${viewYear}-${month}.csv`, headers, csvRows);
  };

  return (
    <div className="monthly-readonly-view">
      <div className="monthly-readonly-view__header">
        <SectionHeader
          title={
            <>
              <span className="monthly-readonly-view__month-full">{getMonthName(viewMonth, locale)}</span>
              <span className="monthly-readonly-view__month-short">{getMonthShort(viewMonth, locale)}</span>
              {' '}{viewYear}
            </>
          }
          onPrev={() => navigate(-1)}
          onNext={() => navigate(1)}
          prevLabel={t({ id: 'year.monthly.previousMonth' })}
          nextLabel={t({ id: 'year.monthly.nextMonth' })}
          actions={
            <>
              {headerActions}
              {hasHabits && (
                <button
                  type="button"
                  className="yearly-export-btn"
                  onClick={exportMonthCsv}
                  aria-label={t({ id: 'stats.export.csv' })}
                >
                  <Download size={16} />
                </button>
              )}
            </>
          }
        />
      </div>

      {isLoading ? (
        <Spinner block />
      ) : !hasHabits ? (
        <p className="monthly-readonly-view__empty-note">
          {t({ id: 'habits.noHabits' })}
        </p>
      ) : (
        <div className="table-container">
          <HabitTable
            habits={allHabits}
            rows={rows}
            getEntry={(habitId, day) =>
              allEntries[habitId]?.find((e: DayEntry) => e.day === day)
            }
            onToggle={(habitId, day) => toggleHabitOn(habitId, day, viewMonth, viewYear)}
            onRate={(habitId, day, r) => setRatingOn(habitId, day, r, viewMonth, viewYear)}
            showDayColumns
            compact
            isDisabledCell={isDisabledCell}
            summaryRow={renderSummary}
            starSize={10}
          />
        </div>
      )}
    </div>
  );
};

export default MonthlyReadOnlyView;